<template>
  <section
    id="comparison"
    class="bg-bg py-16 tablet:py-24"
  >
    <div class="container">
      <div class="mx-auto max-w-3xl">
        <i18n-t
          keypath="landing.comparison.title"
          scope="global"
          tag="h2"
          class="compare-heading text-center text-[28px] font-medium leading-[1.35] tracking-[-0.02em] text-black-100 tablet:text-[42px]"
        >
          <template #accent
            ><span class="text-gold-primary-120">{{ t('landing.comparison.titleAccent') }}</span></template
          >
        </i18n-t>
        <p class="mt-4 text-center text-base font-bold text-black-100 tablet:text-[22px]">
          {{ t('landing.comparison.subtitle') }}
        </p>
      </div>

      <div class="mx-auto mt-10 max-w-5xl tablet:mt-12">
        <!-- Sticky header (desktop only) -->
        <div class="compare-header sticky top-[70px] z-10 hidden grid-cols-[3fr_4fr_5fr] gap-4 bg-bg pb-3 tablet:grid">
          <div class="flex items-center rounded-lg bg-white px-6 py-5 text-[18px] font-bold text-black-80">
            {{ t('landing.comparison.feature') }}
          </div>
          <div class="flex items-center rounded-lg bg-bg px-6 py-5 text-[18px] font-bold text-black-80">
            {{ t('landing.shared.currentBrokerage') }}
          </div>
          <div
            class="flex items-center rounded-lg bg-gold-primary-100 px-6 py-5 text-[22px] font-extrabold"
            style="color: #1a1a1a"
          >
            Insiders of Dubai
          </div>
        </div>

        <!-- Rows -->
        <div>
          <div
            v-for="row in rows"
            :key="row.id"
            class="compare-row grid grid-cols-1 gap-2 pb-3 tablet:grid-cols-[3fr_4fr_5fr] tablet:gap-4"
          >
            <!-- Feature cell (mobile row-header; desktop left column) -->
            <div
              class="rounded-lg bg-white px-5 py-[10px] text-[15px] font-medium text-black-80 tablet:px-6 tablet:py-[14px] tablet:text-[18px]"
            >
              {{ t(`landing.comparisonRows.${row.id}.feature`) }}
            </div>

            <!-- Brokerage + Insiders pair (side-by-side on mobile, display:contents on desktop) -->
            <div class="compare-pair grid grid-cols-2 gap-2 tablet:contents">
              <div
                class="flex flex-col items-center gap-2 rounded-lg bg-bg px-5 py-[10px] text-center text-[13px] leading-[1.4] text-black-80 tablet:flex-row tablet:items-center tablet:gap-[10px] tablet:px-6 tablet:py-[14px] tablet:text-start tablet:text-[17px]"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="size-[22px] shrink-0"
                  style="color: #b0b0b0"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <path
                    d="M8 8l8 8M16 8l-8 8"
                    stroke-linecap="round"
                  />
                </svg>
                <span>{{ t(`landing.comparisonRows.${row.id}.brokerage`) }}</span>
              </div>
              <div
                class="flex flex-col items-center gap-2 rounded-lg bg-gold-primary-10 px-5 py-[10px] text-center text-[13px] font-bold leading-[1.4] text-black-80 tablet:flex-row tablet:items-center tablet:gap-[10px] tablet:px-6 tablet:py-[14px] tablet:text-start tablet:text-[17px]"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="size-[22px] shrink-0"
                  style="color: #10b981"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12l5 5 9-11"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>{{ insidersCell(row.id) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Closing kicker -->
      <div
        class="mx-auto mt-12 max-w-[760px] border-t border-black/[0.08] px-6 py-7 text-center tablet:px-6 tablet:pb-0 tablet:pt-10"
      >
        <div class="text-[13px] font-semibold uppercase tracking-[0.12em] text-black-60">
          {{ t('landing.comparison.kickerTags') }}
        </div>
      </div>

      <div class="mt-10 text-center">
        <NuxtLink
          :to="applyHref"
          @click="trackCtaClick('comparison')"
          class="inline-flex h-14 w-full max-w-[443px] items-center justify-center rounded-xl bg-black-100 px-7 text-base font-bold text-white transition-transform hover:-translate-y-px"
        >
          <span
            >{{ t('landing.shared.applyToJoin') }}
            <span
              class="inline-block rtl:-scale-x-100"
              aria-hidden="true"
              >→</span
            ></span
          >
        </NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const { t } = useI18n();

// Row ids → `landing.comparisonRows.<id>.{feature,brokerage,insiders}`.
const rows = [
  { id: 'paymentSpeed' },
  { id: 'commissionMath' },
  { id: 'availability' },
  { id: 'inventory' },
  { id: 'developerDeals' },
  { id: 'briefings' },
  { id: 'materials' },
  { id: 'ownership' },
  { id: 'accountManager' },
  { id: 'referral' },
  { id: 'flexibility' },
  { id: 'exclusivity' },
  { id: 'minimumVolume' },
] as const;

const { formatNumber } = useFormat();
const { totalProjects } = useLandingFacts();

// Inventory names the live catalogue count; without it the number is dropped, never shown as 0.
const insidersCell = (id: (typeof rows)[number]['id']) => {
  if (id !== 'inventory') return t(`landing.comparisonRows.${id}.insiders`);
  return totalProjects.value === null
    ? t('landing.comparisonRows.inventory.insidersFallback')
    : t('landing.comparisonRows.inventory.insiders', { count: formatNumber(totalProjects.value) });
};

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();
</script>

<style scoped lang="scss">
/* `display: contents` unwraps the mobile pair wrapper so the 3-column desktop grid can
   place the brokerage + Insiders cells directly as grid children. Tailwind's `tablet:contents`
   handles this, but we keep a scoped rule here in case of any downstream purge quirks. */
@media (min-width: 744px) {
  .compare-pair {
    display: contents;
  }
}
</style>
